import {
  type IWebAnalyticsFilter,
  type IWebAnalyticsLastRun,
  type IWebAnalyticsPeriod,
  type IWebAnalyticsTableSettings,
} from '@mahawi/eshop-common';
import { Col, Descriptions, Divider, Row, Skeleton, Space, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import ConsentEstimate from '../components/web-analytics-insights/consent-estimate';
import GapNote from '../components/web-analytics-insights/gap-note';
import InsightsTable, {
  FilterBar,
  QueryPagesDrawer,
} from '../components/web-analytics-insights/insights-table';
import PeriodPicker from '../components/web-analytics-insights/period-picker';
import ProviderStatus, {
  runStatusColor,
} from '../components/web-analytics-insights/provider-status';
import Totals from '../components/web-analytics-insights/totals';
import TrendChart from '../components/web-analytics-insights/trend-chart';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  closeQueryPages,
  loadOverview,
  loadProviderStatus,
  loadQueryPages,
  loadTable,
} from '../reducers/web-analytics-insights/reducer';
import { type IWebAnalyticsOverviewRequest } from '../reducers/web-analytics-insights/types';
import { SPACING } from '../theme/tokens';

const formatDateTime = (value: string | null): string =>
  value ? dayjs(value).format(EDateFormat.DATE_TIME_SHORT) : EMPTY_VALUE;

export const toQuery = (
  { dateFrom, dateTo, compare }: IWebAnalyticsPeriod,
  { provider, searchType, country, device }: IWebAnalyticsFilter,
): IWebAnalyticsOverviewRequest => ({
  dateFrom,
  dateTo,
  compare,
  ...(provider === undefined ? {} : { provider }),
  ...(searchType === undefined ? {} : { searchType }),
  ...(country === undefined ? {} : { country }),
  ...(device === undefined ? {} : { device }),
});

export function LastRunDescription({
  lastRun,
}: {
  lastRun: IWebAnalyticsLastRun | null | undefined;
}): React.ReactElement {
  if (!lastRun) {
    return <Descriptions title="Last collection run" items={[]} />;
  }

  return (
    <Descriptions
      title="Last collection run"
      size="small"
      items={[
        {
          key: 'status',
          label: 'Status',
          children: (
            <Tag color={runStatusColor(lastRun.status)}>{lastRun.status}</Tag>
          ),
        },
        {
          key: 'startedAt',
          label: 'Started',
          children: formatDateTime(lastRun.startedAt),
        },
        {
          key: 'finishedAt',
          label: 'Finished',
          children: formatDateTime(lastRun.finishedAt),
        },
      ]}
    />
  );
}

function WebAnalyticsInsightsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const insights = useSelector((s: RootState) => s.WebAnalyticsInsights);
  const { query, tableSettings } = insights;

  const load = (
    nextQuery: IWebAnalyticsOverviewRequest,
    nextSettings: IWebAnalyticsTableSettings,
  ): void => {
    dispatch(loadOverview(nextQuery));
    dispatch(loadTable({ ...nextQuery, ...nextSettings }));
  };

  const reload = (): void => {
    dispatch(loadProviderStatus());
    load(query, tableSettings);
  };

  useEffect((): void => {
    reload();
  }, [dispatch]);

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader
        title="Web analytics insights"
        onReload={reload}
        isReloadDisabled={insights.overviewInProcess || insights.tableInProcess}
      />

      <PeriodPicker
        period={query}
        disabled={insights.overviewInProcess}
        onChange={(period: IWebAnalyticsPeriod): void => {
          load(toQuery(period, query), { ...tableSettings, page: 1 });
        }}
      />

      <FilterBar
        filter={query}
        kind={tableSettings.kind}
        disabled={insights.overviewInProcess}
        onChange={(filter: IWebAnalyticsFilter): void => {
          load(toQuery(query, filter), { ...tableSettings, page: 1 });
        }}
      />

      {insights.overview ? (
        <Space
          orientation="vertical"
          size={SPACING.lg}
          style={{ width: '100%' }}
        >
          <GapNote
            gaps={insights.overview.gaps}
            ignoredFilters={insights.overview.ignoredFilters}
          />
          <Totals totals={insights.overview.totals} />
          <TrendChart
            series={insights.overview.series}
            compareSeries={insights.overview.compareSeries}
          />
          <Row gutter={[SPACING.lg, SPACING.lg]}>
            <Col xs={24} xl={12}>
              <ConsentEstimate estimate={insights.overview.consentEstimate} />
            </Col>
          </Row>
        </Space>
      ) : (
        <Skeleton active loading={insights.overviewInProcess} />
      )}

      <Divider titlePlacement="start">Breakdown</Divider>

      <InsightsTable
        settings={tableSettings}
        filter={query}
        table={insights.table}
        isLoading={insights.tableInProcess}
        onChange={(settings: IWebAnalyticsTableSettings): void => {
          dispatch(loadTable({ ...query, ...settings }));
        }}
        onDrillDown={(request): void => {
          dispatch(loadQueryPages(request));
        }}
      />

      <QueryPagesDrawer
        request={insights.queryPagesRequest}
        result={insights.queryPages}
        isLoading={insights.queryPagesInProcess}
        onClose={(): void => {
          dispatch(closeQueryPages());
        }}
      />

      <Divider titlePlacement="start">Data collection</Divider>

      <LastRunDescription lastRun={insights.lastRun} />

      <ProviderStatus
        providers={insights.providers ?? []}
        isLoading={insights.inProcess && !insights.providers}
      />
    </Space>
  );
}

export default WebAnalyticsInsightsContainer;
